import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock, Mail, LogIn, Shield, Cpu, Network, Hexagon, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { useAuth } from '@/shared/hooks/useAuth';
import { AuthApiService } from '@/features/auth/api/authApi';
import type { LoginCredentials } from '@/shared/lib/types';
import { getCurrentTenantSubdomain, getGenericPortalUrl } from '@/shared/lib/utils';
import { authService } from '@/shared/lib/auth';
import LoginFeatures from './LoginFeatures';
import LoginLogo from './LoginLogo';

type LoginView = 'login' | 'forgot' | 'forgot-success';

const Login: React.FC = () => {
  const { t } = useTranslation('auth');
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // View state: login form or forgot password form
  const [view, setView] = useState<LoginView>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);

  // DVD bouncing logo state
  const [dvdPosition, setDvdPosition] = useState({ x: 100, y: 100 });
  const [dvdVelocity, setDvdVelocity] = useState({ dx: 2, dy: 1.5 });
  const [dvdRotation, setDvdRotation] = useState(0);
  const [dvdLogoError, setDvdLogoError] = useState(false);
  const animationFrameRef = useRef<number>();
  const logoSize = 80;

  const [tenantName, setTenantName] = useState<string | null>(null);
  const [checking, setChecking] = useState<boolean>(() => getCurrentTenantSubdomain() !== null);

  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // DVD bouncing animation
  useEffect(() => {
    const animate = () => {
      setDvdPosition(prev => {
        let newX = prev.x + dvdVelocity.dx;
        let newY = prev.y + dvdVelocity.dy;
        let newDx = dvdVelocity.dx;
        let newDy = dvdVelocity.dy;

        // Bounce off edges
        if (newX <= 0 || newX >= window.innerWidth - logoSize) {
          newDx = -newDx;
          newX = Math.max(0, Math.min(newX, window.innerWidth - logoSize));
        }
        if (newY <= 0 || newY >= window.innerHeight - logoSize) {
          newDy = -newDy;
          newY = Math.max(0, Math.min(newY, window.innerHeight - logoSize));
        }

        if (newDx !== dvdVelocity.dx || newDy !== dvdVelocity.dy) {
          setDvdVelocity({ dx: newDx, dy: newDy });
        }

        return { x: newX, y: newY };
      });

      setDvdRotation(prev => (prev + 1) % 360);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [dvdVelocity]);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = location.state?.from?.pathname ?? '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location.state]);

  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [credentials.email, credentials.password, clearError, error]);

  useEffect(() => {
    const sub = getCurrentTenantSubdomain();
    if (!sub) return;
    let active = true;
    authService.getTenantBySubdomain(sub).then(result => {
      if (!active) return;
      if (result.status === 'found') {
        setTenantName(result.tenant.name);
        setChecking(false);
      } else if (result.status === 'not-found') {
        const url = getGenericPortalUrl();
        if (url) {
          window.location.replace(url);
          return;
        }
        setChecking(false);
      } else {
        setChecking(false);
      }
    }).catch(() => {
      if (active) setChecking(false);
    });
    return () => { active = false; };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRememberMeChange = (checked: boolean) => {
    setCredentials(prev => ({
      ...prev,
      rememberMe: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!credentials.email.trim() || !credentials.password.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await login(credentials);
      if (result.requiresMfa) {
        navigate(result.requiresSetup ? '/mfa/setup' : '/login/mfa', { replace: true });
      }
    } catch {
      // error is handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotEmail.trim()) {
      return;
    }

    setIsForgotSubmitting(true);
    setForgotError(null);

    try {
      await AuthApiService.requestPasswordReset(forgotEmail);
      setView('forgot-success');
    } catch (err: any) {
      setForgotError(err?.response?.data?.message || t('shared.requestResetErrorFallback'));
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    setView('login');
    setForgotEmail('');
    setForgotError(null);
  };

  const isFormValid = credentials.email.trim() !== '' && credentials.password.trim() !== '';

  if (isLoading || checking) {
    return (
      <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
          <p className="text-gray-400">{t('common:feedback.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f2e] relative overflow-hidden flex items-center justify-center dark">
      {/* Mouse-following glow effect */}
      <div
        className="pointer-events-none fixed w-[500px] h-[500px] rounded-full transition-transform duration-100 ease-out"
        style={{
          left: mousePosition.x - 250,
          top: mousePosition.y - 250,
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.15) 0%, rgba(0, 255, 255, 0.05) 40%, transparent 70%)',
          filter: 'blur(60px)',
          zIndex: 0
        }}
      />

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      {/* DVD-style bouncing logo */}
      <div
        className="pointer-events-none fixed z-10"
        style={{
          left: dvdPosition.x,
          top: dvdPosition.y,
          width: logoSize,
          height: logoSize,
          transform: `rotate(${dvdRotation}deg)`,
        }}
      >
        {dvdLogoError ? (
          <Hexagon className="w-full h-full text-cyan-400/20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]" />
        ) : (
          <img
            src="/logo-invexis-neon-short.png"
            alt=""
            className="w-full h-full object-contain opacity-20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]"
            onError={() => setDvdLogoError(true)}
          />
        )}
      </div>

      {/* Floating icons with bobbing animation */}
      <div
        className="absolute top-20 left-20 text-cyan-400/20 animate-[float_6s_ease-in-out_infinite]"
      >
        <Hexagon className="w-16 h-16" />
      </div>
      <div
        className="absolute top-40 right-32 text-primary/20 animate-[float_8s_ease-in-out_infinite_1s]"
      >
        <Shield className="w-12 h-12" />
      </div>
      <div
        className="absolute bottom-32 left-40 text-cyan-400/15 animate-[float_7s_ease-in-out_infinite_0.5s]"
      >
        <Cpu className="w-14 h-14" />
      </div>
      <div
        className="absolute bottom-20 right-20 text-primary/15 animate-[float_9s_ease-in-out_infinite_1.5s]"
      >
        <Network className="w-20 h-20" />
      </div>

      {/* Blurred background circles */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-400/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-[100px]" />

      {/* Card */}
      <div className="relative z-20 rounded-2xl p-8 md:p-12 max-w-md w-full mx-4 border border-white/10 bg-[#20263a]/95 animate-fade-in">
        <LoginLogo />

        {view === 'login' && (
          <>
            {/* Subtitle */}
            <div className="text-center mb-8">
              {tenantName
                ? <p className="text-gray-300 text-sm">{t('login.tenantSubtitle')}<span className="font-medium">{tenantName}</span></p>
                : <p className="text-gray-400 text-sm">{t('login.genericSubtitle')}</p>}
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-300">
                  {t('login.emailLabel')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={credentials.email}
                    onChange={handleInputChange}
                    placeholder={t('shared.emailPlaceholder')}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                    required
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-300">
                  {t('login.passwordLabel')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={credentials.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                    required
                    autoComplete="current-password"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    disabled={isSubmitting}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rememberMe"
                    checked={credentials.rememberMe}
                    onCheckedChange={handleRememberMeChange}
                    disabled={isSubmitting}
                    className="border-white/20 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                  />
                  <Label
                    htmlFor="rememberMe"
                    className="text-sm text-gray-400 cursor-pointer"
                  >
                    {t('login.rememberMe')}
                  </Label>
                </div>
                <button
                  type="button"
                  onClick={() => setView('forgot')}
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  {t('shared.forgotPasswordQuestion')}
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-primary hover:from-cyan-400 hover:to-primary/90 text-background shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-background"></div>
                    <span>{t('login.submitting')}</span>
                  </div>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    {t('login.submitButton')}
                  </>
                )}
              </Button>
            </form>

            <LoginFeatures />
          </>
        )}

        {view === 'forgot' && (
          <>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">{t('shared.forgotPasswordQuestion')}</h2>
              <p className="text-gray-400 text-sm">
                {t('shared.forgotPasswordInstructions')}
              </p>
            </div>

            <form onSubmit={handleForgotSubmit} className="space-y-6">
              {forgotError && (
                <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
                  <AlertDescription>{forgotError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="forgotEmail" className="text-sm font-medium text-gray-300">
                  {t('login.emailLabel')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                  <Input
                    id="forgotEmail"
                    name="forgotEmail"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder={t('shared.emailPlaceholder')}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                    required
                    autoComplete="email"
                    disabled={isForgotSubmitting}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-primary hover:from-cyan-400 hover:to-primary/90 text-background shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40"
                disabled={!forgotEmail.trim() || isForgotSubmitting}
              >
                {isForgotSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-background"></div>
                    <span>{t('shared.sendingLabel')}</span>
                  </div>
                ) : (
                  t('shared.sendRecoveryEmailButton')
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  <ArrowLeft className="inline-block mr-1 h-3 w-3" />
                  {t('shared.backToLoginLink')}
                </button>
              </div>
            </form>
          </>
        )}

        {view === 'forgot-success' && (
          <>
            <div className="flex flex-col items-center mb-6">
              <CheckCircle2 className="h-16 w-16 text-emerald-400 mb-4 drop-shadow-[0_0_10px_rgba(52,211,153,0.4)]" />
              <h2 className="text-2xl font-bold text-white">{t('shared.emailSentTitle')}</h2>
              <p className="mt-2 text-sm text-gray-400 text-center">
                {t('shared.emailSentDescription')}
              </p>
            </div>

            <div className="space-y-4">
              <Alert className="bg-cyan-400/10 border-cyan-400/30">
                <AlertDescription className="text-cyan-300">
                  {t('shared.checkInboxNotice')}
                </AlertDescription>
              </Alert>

              <Button
                variant="outline"
                className="w-full h-12 text-base font-semibold border-white/10 text-gray-300 hover:bg-white/5 hover:text-white"
                onClick={handleBackToLogin}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('shared.backToLoginLink')}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-cyan-400/20 rounded-tl-3xl" />
      <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-primary/20 rounded-tr-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-primary/20 rounded-bl-3xl" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-cyan-400/20 rounded-br-3xl" />
    </div>
  );
};

export default Login;
